/**
 * features/clients/ClientsManager.tsx
 * ------------------------------------------------------------------
 * Phase 2 — Clients. List, search, create/edit (with a contacts
 * editor), and archive/restore. Clients are the parties invoices,
 * quotes, and statements are addressed to; the buyer TIN/VAT fields
 * here feed the dormant-VAT snapshot taken at issue time.
 * ------------------------------------------------------------------
 */
import { useMemo, useState } from 'react';
import {
  Users,
  Plus,
  Pencil,
  X,
  Search,
  Star,
  Trash2,
  RotateCcw,
  Building2,
  User as UserIcon,
  GraduationCap,
  Mail,
  Phone,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useClients } from './useClients';
import { createClient, updateClient, setClientActive, type ClientInput } from './clientsWrite';
import type { Client, ClientType, ClientContact } from '@/types';
import { Button, Card, Field, Input, Select, Textarea, Badge, Spinner } from '@/components/ui/primitives';
import { glassSurface } from '@/components/ui/glass';

const TYPE_META: Record<ClientType, { label: string; icon: typeof Building2; tone: 'brand' | 'neutral' }> = {
  school: { label: 'School', icon: GraduationCap, tone: 'brand' },
  company: { label: 'Company', icon: Building2, tone: 'neutral' },
  individual: { label: 'Individual', icon: UserIcon, tone: 'neutral' },
  other: { label: 'Other', icon: Building2, tone: 'neutral' },
};

function emptyForm(): ClientInput {
  return {
    name: '',
    type: 'company',
    tin: '',
    vatNumber: '',
    registrationNumber: '',
    email: '',
    phone: '',
    contacts: [],
    billingAddress: '',
    notes: '',
    active: true,
  };
}

function fromClient(c: Client): ClientInput {
  return {
    name: c.name,
    type: c.type,
    tin: c.tin ?? '',
    vatNumber: c.vatNumber ?? '',
    registrationNumber: c.registrationNumber ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    contacts: c.contacts ?? [],
    billingAddress: c.billingAddress ?? '',
    notes: c.notes ?? '',
    active: c.active,
  };
}

/** Drop empty optional strings so we don't persist blank fields. */
function clean(input: ClientInput): ClientInput {
  const out: ClientInput = {
    name: input.name.trim(),
    type: input.type,
    contacts: input.contacts
      .filter((k) => k.name.trim())
      .map((k) => ({
        name: k.name.trim(),
        ...(k.title?.trim() ? { title: k.title.trim() } : {}),
        ...(k.email?.trim() ? { email: k.email.trim() } : {}),
        ...(k.phone?.trim() ? { phone: k.phone.trim() } : {}),
        ...(k.primary ? { primary: true } : {}),
      })),
    active: input.active,
  };
  if (input.tin?.trim()) out.tin = input.tin.trim();
  if (input.vatNumber?.trim()) out.vatNumber = input.vatNumber.trim();
  if (input.registrationNumber?.trim()) out.registrationNumber = input.registrationNumber.trim();
  if (input.email?.trim()) out.email = input.email.trim();
  if (input.phone?.trim()) out.phone = input.phone.trim();
  if (input.billingAddress?.trim()) out.billingAddress = input.billingAddress.trim();
  if (input.notes?.trim()) out.notes = input.notes.trim();
  return out;
}

export function ClientsManager() {
  const { user } = useAuth();
  const { clients, loading } = useClients();

  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null); // null = closed
  const [isNew, setIsNew] = useState(false);
  const [form, setForm] = useState<ClientInput>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients
      .filter((c) => (showArchived ? true : c.active))
      .filter(
        (c) =>
          !q ||
          c.name.toLowerCase().includes(q) ||
          (c.email ?? '').toLowerCase().includes(q),
      );
  }, [clients, search, showArchived]);

  function openNew() {
    setForm(emptyForm());
    setIsNew(true);
    setEditingId('new');
    setError('');
  }

  function openEdit(c: Client) {
    setForm(fromClient(c));
    setIsNew(false);
    setEditingId(c.id);
    setError('');
  }

  function close() {
    setEditingId(null);
    setError('');
  }

  function patch<K extends keyof ClientInput>(key: K, value: ClientInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function patchContact(i: number, key: keyof ClientContact, value: string | boolean) {
    setForm((f) => ({
      ...f,
      contacts: f.contacts.map((c, j) => (j === i ? { ...c, [key]: value } : c)),
    }));
  }

  function addContact() {
    setForm((f) => ({ ...f, contacts: [...f.contacts, { name: '' }] }));
  }

  function removeContact(i: number) {
    setForm((f) => ({ ...f, contacts: f.contacts.filter((_, j) => j !== i) }));
  }

  async function save() {
    if (!form.name.trim()) {
      setError('A client name is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = clean(form);
      if (isNew) {
        await createClient(payload, user?.email ?? user?.uid ?? 'unknown');
      } else if (editingId) {
        await updateClient(editingId, payload);
      }
      close();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the client.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search clients…"
            className="pl-9"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-primaryLight dark:text-gray-300">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="h-4 w-4 accent-brand"
          />
          Show archived
        </label>
        <Button icon={<Plus size={16} />} onClick={openNew}>
          New client
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <Spinner label="Loading clients…" />
      ) : visible.length === 0 ? (
        <Card className="py-16 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-brandSoft text-brand">
            <Users size={22} />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {search ? 'No clients match your search.' : 'No clients yet — add your first one.'}
          </p>
        </Card>
      ) : (
        <div className="grid gap-3">
          {visible.map((c) => {
            const meta = TYPE_META[c.type];
            const Icon = meta.icon;
            return (
              <Card key={c.id} accent={c.active} className="flex items-center gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brandSoft text-brand">
                  <Icon size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-semibold text-primary dark:text-gray-100">
                      {c.name}
                    </span>
                    <Badge tone={meta.tone}>{meta.label}</Badge>
                    {!c.active && <Badge tone="warning">Archived</Badge>}
                  </div>
                  <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-primaryLight dark:text-gray-400">
                    {c.email && (
                      <span className="inline-flex items-center gap-1">
                        <Mail size={12} /> {c.email}
                      </span>
                    )}
                    {c.phone && (
                      <span className="inline-flex items-center gap-1">
                        <Phone size={12} /> {c.phone}
                      </span>
                    )}
                    {c.tin && <span>TIN {c.tin}</span>}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    onClick={() => openEdit(c)}
                    className="rounded-lg p-2 text-primaryLight transition hover:bg-black/5 hover:text-brand dark:text-gray-300 dark:hover:bg-white/5"
                    aria-label="Edit"
                    title="Edit"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => void setClientActive(c.id, !c.active)}
                    className="rounded-lg p-2 text-primaryLight transition hover:bg-black/5 hover:text-brand dark:text-gray-300 dark:hover:bg-white/5"
                    aria-label={c.active ? 'Archive' : 'Restore'}
                    title={c.active ? 'Archive' : 'Restore'}
                  >
                    {c.active ? <Trash2 size={16} /> : <RotateCcw size={16} />}
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Editor modal */}
      {editingId && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-md">
          <div className="my-8 w-full max-w-2xl">
            <div className={glassSurface('space-y-4 p-5')}>
              <div className="-mx-5 -mt-5 mb-1 flex items-center justify-between rounded-t-card bg-brand px-5 py-3.5">
                <div className="flex items-center gap-2 text-white">
                  <Users size={18} />
                  <h2 className="text-base font-semibold text-white">
                    {isNew ? 'New client' : 'Edit client'}
                  </h2>
                </div>
                <button
                  onClick={close}
                  className="rounded-lg p-1 text-white/80 transition hover:bg-white/15 hover:text-white"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Name">
                  <Input value={form.name} onChange={(e) => patch('name', e.target.value)} />
                </Field>
                <Field label="Type">
                  <Select value={form.type} onChange={(e) => patch('type', e.target.value as ClientType)}>
                    <option value="company">Company</option>
                    <option value="school">School</option>
                    <option value="individual">Individual</option>
                    <option value="other">Other</option>
                  </Select>
                </Field>
                <Field label="Email">
                  <Input type="email" value={form.email} onChange={(e) => patch('email', e.target.value)} />
                </Field>
                <Field label="Phone">
                  <Input value={form.phone} onChange={(e) => patch('phone', e.target.value)} />
                </Field>
                <Field label="TIN" hint="Buyer tax number (for BURS invoices)">
                  <Input value={form.tin} onChange={(e) => patch('tin', e.target.value)} />
                </Field>
                <Field label="VAT number" hint="If the client is VAT-registered">
                  <Input value={form.vatNumber} onChange={(e) => patch('vatNumber', e.target.value)} />
                </Field>
                <Field label="Registration number">
                  <Input
                    value={form.registrationNumber}
                    onChange={(e) => patch('registrationNumber', e.target.value)}
                  />
                </Field>
              </div>

              <Field label="Billing address">
                <Textarea
                  rows={2}
                  value={form.billingAddress}
                  onChange={(e) => patch('billingAddress', e.target.value)}
                />
              </Field>

              {/* Contacts */}
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-primaryLight dark:text-gray-400">
                    Contacts
                  </span>
                  <button
                    type="button"
                    onClick={addContact}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline"
                  >
                    <Plus size={13} /> Add contact
                  </button>
                </div>
                <div className="space-y-2">
                  {form.contacts.map((k, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-1 gap-2 rounded-xl border border-black/5 p-2.5 sm:grid-cols-[1fr_1fr_auto] dark:border-white/10"
                    >
                      <Input
                        placeholder="Name"
                        value={k.name}
                        onChange={(e) => patchContact(i, 'name', e.target.value)}
                      />
                      <Input
                        placeholder="Email"
                        value={k.email ?? ''}
                        onChange={(e) => patchContact(i, 'email', e.target.value)}
                      />
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => patchContact(i, 'primary', !k.primary)}
                          title="Primary contact"
                          className={`rounded-lg p-2 transition ${
                            k.primary
                              ? 'bg-brandSoft text-brand'
                              : 'text-gray-400 hover:bg-black/5 dark:hover:bg-white/5'
                          }`}
                        >
                          <Star size={16} fill={k.primary ? 'currentColor' : 'none'} />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeContact(i)}
                          className="rounded-lg p-2 text-gray-400 transition hover:bg-black/5 hover:text-red-600 dark:hover:bg-white/5"
                          aria-label="Remove contact"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                  {form.contacts.length === 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      No contacts. The starred contact receives invoices and statements by default.
                    </p>
                  )}
                </div>
              </div>

              <Field label="Notes">
                <Textarea rows={2} value={form.notes} onChange={(e) => patch('notes', e.target.value)} />
              </Field>

              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={close}>
                  Cancel
                </Button>
                <Button onClick={save} loading={saving}>
                  {isNew ? 'Create client' : 'Save changes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
